import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const roleLabel = {
  admin: "Admin",
  client_team: "Client Team",
  partner_team: "Partner Team",
};

export default function TeamPage() {
  const { user: current } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "client_team" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!form.email || !form.name) return toast.error("Name and email are required");
    try {
      if (form.password) {
        // Legacy path — creates a user with a temporary password (email/password login still works).
        await api.post("/users", form);
        toast.success("Team member added with a password");
      } else {
        // Google-only invite — the invitee signs in via Google using this exact email.
        const { data } = await api.post("/users/invite", {
          email: form.email,
          name: form.name,
          role: form.role,
        });
        if (data.email_sent) {
          toast.success("Invite email sent · they can sign in with Google");
        } else if (data.email_enabled === false) {
          toast.success("Invite created · share your login URL with them (email disabled — add RESEND_API_KEY to enable)");
        } else {
          toast.warning("Invite created but email delivery failed — please notify them manually");
        }
      }
      setOpen(false);
      setForm({ email: "", name: "", password: "", role: "client_team" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow">Access control</div>
          <h1 className="font-display font-semibold text-xl sm:text-2xl tracking-tight mt-1">Team</h1>
          <p className="text-sm text-ink-2 mt-1">
            Client-team members see only client invoices. Partner-team members see only partner invoices. Admins see everything.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="rounded-sm bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white"
              data-testid="add-user-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Invite member
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display">Invite a team member</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="eyebrow">Full name</Label>
                <Input
                  className="rounded-sm mt-1"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="user-name-input"
                />
              </div>
              <div>
                <Label className="eyebrow">Email</Label>
                <Input
                  className="rounded-sm mt-1"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="user-email-input"
                />
              </div>
              <div>
                <Label className="eyebrow">Temporary password <span className="text-ink-2 font-normal normal-case text-[10px]">(optional — leave empty for Google-only sign-in)</span></Label>
                <Input
                  className="rounded-sm mt-1"
                  type="password"
                  placeholder="Leave empty to invite via Google"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="user-password-input"
                />
              </div>
              <div>
                <Label className="eyebrow">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client_team">Client Team</SelectItem>
                    <SelectItem value="partner_team">Partner Team</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" className="rounded-sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={save}
                className="rounded-sm bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white"
                data-testid="user-save-btn"
              >
                {form.password ? "Add with password" : "Send Google invite"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-line bg-card overflow-x-auto">
        <table className="w-full text-[13px] min-w-[560px]">
          <thead>
            <tr className="border-b border-line text-left">
              <th className="px-4 py-2.5 eyebrow">Name</th>
              <th className="px-4 py-2.5 eyebrow">Email</th>
              <th className="px-4 py-2.5 eyebrow">Role</th>
              <th className="px-4 py-2.5 eyebrow text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="users-table">
            {users.map((u) => (
              <tr key={u.id} className="row-divider hover:bg-[color:var(--bone)]">
                <td className="px-4 py-2.5">
                  {u.name}
                  {u.id === current?.id && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-forest">you</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-ink-2 tabular">{u.email}</td>
                <td className="px-4 py-2.5">
                  <span className="inline-block text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 bg-[color:var(--bone)] border border-line">
                    {roleLabel[u.role] || u.role}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-sm h-7 px-2"
                    title="Generate reset link"
                    data-testid={`reset-link-${u.id}`}
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/users/${u.id}/reset-link`);
                        try {
                          await navigator.clipboard.writeText(data.reset_url);
                          toast.success(`Reset link copied. Valid until ${new Date(data.expires_at).toLocaleString("en-IN")}`);
                        } catch {
                          window.prompt("Copy this reset link:", data.reset_url);
                        }
                      } catch (e) {
                        toast.error(formatApiError(e));
                      }
                    }}
                  >
                    <KeyRound className="w-3.5 h-3.5" />
                  </Button>
                  {u.id !== current?.id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-sm h-7 px-2 text-[color:var(--overdue)]"
                      onClick={() => remove(u)}
                      data-testid={`delete-user-${u.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
